import api from "./client";

export const courseFallbackImage =
  "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function isNumericReference(value) {
  return /^\d+$/.test(String(value ?? "").trim());
}

function unwrapData(response) {
  if (response && typeof response === "object" && "data" in response) {
    return response.data;
  }

  return response;
}

export function getCourseTitle(course, isAr = false) {
  if (!course) return "";
  return isAr
    ? course.title_ar || course.title_en || `Course #${course.id ?? ""}`
    : course.title_en || course.title_ar || `Course #${course.id ?? ""}`;
}

export function getCourseSummary(course, isAr = false) {
  if (!course) return "";
  return isAr
    ? course.short_description_ar || course.description_ar || course.short_description_en || course.description_en || ""
    : course.short_description_en || course.description_en || course.short_description_ar || course.description_ar || "";
}

export function getCourseDescription(course, isAr = false) {
  if (!course) return "";
  return isAr
    ? course.description_ar || course.description_en || ""
    : course.description_en || course.description_ar || "";
}

export function getCourseLearnList(course, isAr = false) {
  if (!course) return "";
  return isAr
    ? course.what_you_will_learn_ar || course.what_you_will_learn_en || ""
    : course.what_you_will_learn_en || course.what_you_will_learn_ar || "";
}

export function getCourseRequirements(course, isAr = false) {
  if (!course) return "";
  return isAr
    ? course.requirements_ar || course.requirements_en || ""
    : course.requirements_en || course.requirements_ar || "";
}

export function getCoursePrice(course) {
  const salePrice = Number(course?.sale_price ?? 0);
  if (salePrice > 0) return salePrice;
  return Number(course?.price ?? 0);
}

export function getCourseImage(course) {
  const candidates = [
    course?.thumbnail,
    course?.image,
    course?.cover_image,
    course?.thumbnail_url,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  if (candidates.length > 0) {
    return { src: candidates[0], isFallback: false };
  }

  return { src: courseFallbackImage, isFallback: true };
}

export function formatCurrency(amount, currency = "USD") {
  const value = Number(amount ?? 0);

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: String(currency || "USD").toUpperCase(),
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    return `${value.toFixed(value % 1 === 0 ? 0 : 2)} ${String(currency || "USD").toUpperCase()}`;
  }
}

export function getLessonTitle(lesson, isAr = false) {
  if (!lesson) return "";
  return isAr
    ? lesson.title_ar || lesson.title_en || `Lesson #${lesson.id ?? ""}`
    : lesson.title_en || lesson.title_ar || `Lesson #${lesson.id ?? ""}`;
}

export function flattenCourseLessons(course) {
  const sections = asArray(course?.sections);
  return sections.flatMap((section) =>
    asArray(section?.lessons).map((lesson) => ({
      ...lesson,
      section_title_ar: section?.title_ar || "",
      section_title_en: section?.title_en || "",
    }))
  );
}

export function findLessonById(course, lessonId) {
  const targetId = Number(lessonId);
  if (!targetId) return null;
  return flattenCourseLessons(course).find((lesson) => Number(lesson.id) === targetId) || null;
}

export function getEmbedVideoUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";

  const youtubeWatch = raw.match(
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/)([\w-]{6,})/i
  );
  if (youtubeWatch?.[1]) {
    return `https://www.youtube.com/embed/${youtubeWatch[1]}`;
  }

  const vimeoMatch = raw.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeoMatch?.[1]) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  return "";
}

export function isDirectVideoFile(url) {
  return /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(String(url || "").trim());
}

export function formatLessonDuration(seconds) {
  const value = Number(seconds ?? 0);
  if (!value) return "";

  const hrs = Math.floor(value / 3600);
  const mins = Math.floor((value % 3600) / 60);
  const secs = value % 60;

  if (hrs > 0) {
    return `${hrs}h ${mins}m`;
  }

  if (mins > 0) {
    return `${mins}m ${secs > 0 ? `${secs}s` : ""}`.trim();
  }

  return `${secs}s`;
}

export async function listCourses(options = {}) {
  const response = await api.apiGet("courses", {
    cache: options.cache ?? true,
    ttl: options.ttl ?? 30_000,
  });

  return asArray(unwrapData(response));
}

export async function resolveCourseId(courseReference, knownCourses = null) {
  const raw = String(courseReference ?? "").trim();

  if (isNumericReference(raw)) {
    return Number(raw);
  }

  const courses = knownCourses || (await listCourses({ cache: true, ttl: 15_000 }));
  const normalized = raw.toLowerCase();
  const matched = courses.find(
    (course) =>
      String(course?.slug || "").toLowerCase() === normalized ||
      String(course?.id || "") === raw
  );

  if (!matched?.id) {
    throw { status: 404, message: "Course not found" };
  }

  return Number(matched.id);
}

export async function getCourseDetails(courseReference) {
  let resolvedId = null;

  if (isNumericReference(courseReference)) {
    resolvedId = Number(courseReference);
  } else {
    resolvedId = await resolveCourseId(courseReference);
  }

  const response = await api.apiGet(`courses/${resolvedId}`);
  return unwrapData(response);
}

export async function listCourseReviews(courseReference) {
  const resolvedId = isNumericReference(courseReference)
    ? Number(courseReference)
    : await resolveCourseId(courseReference);

  const response = await api.apiGet(`courses/${resolvedId}/reviews`, {
    cache: true,
    ttl: 15_000,
  });

  return asArray(unwrapData(response));
}

export async function getMyCourses() {
  const response = await api.apiGet("my-courses");
  return asArray(unwrapData(response));
}

export async function submitCourseReview(courseId, payload = {}) {
  const response = await api.apiPost(`courses/${courseId}/reviews`, payload);
  return unwrapData(response);
}

export async function enrollInCourse(courseId) {
  return api.apiPost(`courses/${courseId}/enroll`, {});
}

export async function createCourseCheckout(courseId, payload = {}) {
  return api.apiPost(`courses/${courseId}/checkout`, payload);
}

export async function getCourseLearnPayload(courseId) {
  const response = await api.apiGet(`courses/${courseId}/learn`);
  return unwrapData(response);
}

export async function getCourseLesson(courseId, lessonId) {
  const response = await api.apiGet(`courses/${courseId}/lessons/${lessonId}`);
  return unwrapData(response);
}

export async function saveCourseLessonProgress(courseId, lessonId, payload = {}) {
  return api.apiPost(`courses/${courseId}/lessons/${lessonId}/progress`, payload);
}

export async function completeCourseLesson(courseId, lessonId) {
  return api.apiPost(`courses/${courseId}/lessons/${lessonId}/complete`, {});
}

export async function confirmCoursePayment({ sessionId = "", token = "", provider = "", mode = "" } = {}) {
  const params = new URLSearchParams();

  if (sessionId) params.set("session_id", sessionId);
  if (token) params.set("token", token);
  if (provider) params.set("provider", provider);
  if (mode) params.set("mode", mode);

  const response = await api.apiGet(`courses/payment/success?${params.toString()}`);
  return unwrapData(response);
}
